/**
 * The busy state of a Save button, owned by the tap that set it.
 *
 * PR #1398 review (Codex, "Keep busy state scoped to the active save"). Every
 * Save button frees itself when the item it shows changes, so a save for the
 * NEW item can start while the old item is still loading. That old save then
 * finished in its own `finally` and freed the button as well, in the middle of
 * the new item's save. A third tap started that same save again: two fetches
 * and two share sheets for one video, proven in
 * tests/harness/save-video/check.mjs (case G) before this existed.
 *
 * PR #1387 review: the first version retired the save in flight whenever the
 * item changed, so an item that changed and then changed BACK while its save
 * was still loading freed the button for that same item. A second tap then
 * saved it again, and the first save, current once more, delivered too: two
 * share sheets for one video (case H in the same harness).
 *
 * So each tap takes a numbered attempt that remembers which item it is for,
 * and the button is busy exactly while an attempt for the item now shown is
 * still running. A different item frees the button for itself; the item
 * coming back finds its own save still running and is busy again, so one tap
 * saves once. Only an attempt's own `finally` ends it, so a save finishing
 * late for another item never frees this one. Shared by SaveVideoButton, the
 * message AttachmentSaveButton and the public class page's Download video, so
 * the rule lives in one place.
 *
 * Whether a finished save is DELIVERED is still decided by
 * prepareAndDeliverCurrentSave (lib/saveLifecycle.ts): a save whose item is
 * no longer on screen is dropped before share() or click().
 */
import { useCallback, useRef, useState } from 'react';

export function useSaveBusy(saveIdentity: string): {
  busy: boolean;
  /** Mark the button busy for a new tap on the item shown. Returns that tap's attempt. */
  beginAttempt: () => number;
  /** End `attempt`. The button stays busy while its item has another save running. */
  endAttempt: (attempt: number) => void;
} {
  // Each save still running, by attempt number, with the item it is for.
  const [running, setRunning] = useState<ReadonlyMap<number, string>>(() => new Map());
  const lastAttempt = useRef(0);

  const beginAttempt = useCallback(() => {
    lastAttempt.current += 1;
    const attempt = lastAttempt.current;
    setRunning((current) => new Map(current).set(attempt, saveIdentity));
    return attempt;
  }, [saveIdentity]);

  const endAttempt = useCallback((attempt: number) => {
    setRunning((current) => {
      if (!current.has(attempt)) return current;
      const next = new Map(current);
      next.delete(attempt);
      return next;
    });
  }, []);

  const busy = Array.from(running.values()).includes(saveIdentity);
  return { busy, beginAttempt, endAttempt };
}
